import React from 'react'
import { YapContentPreview } from '@yapstudios/yap-content-builder'
import { styles, fonts } from "./CBStyles.js"
import { institutionComponents } from './CollegeboardInstitutionLibrary.js'
import { mapAriaHiddenArray } from './isAriaHidden.js'

export interface CBInstitutionPreviewProps {

    /**
     * Name describing this preview.
     */ 
    name: string;

    /**
     * Width to render the preview at
     */ 
    width: number;

    /**
     * Main title shown in the preview
     */ 
    title: string;

    /**
     * Screen reader description for title
     * If not provided the title element will be aria-hidden
     */ 
    titleDescription?: string;

    /**
     * Subtitle shown in the preview
     */ 
    subtitle: string;

    /**
     * Screen reader description for subtitle
     * If not provided the subtitle element will be aria-hidden
     */    
    subtitleDescription?: string;

    /**
     * Description shown in the preview
     */    
    description: string;

    /**
     * Logo shown in the preview.
     */    
    logo: string;

    /**
     * Screen reader description for logo.
     * If not provided the logo element will be aria-hidden
     */    
    logoDescription?: string;

    /**
     * Background image
     */    
    background: string;

    /**
     * Screen reader description for background image. 
     * If not provided the background element will be aria-hidden
     */       
    backgroundDescription?: string;

    /**
     * Elements to hide from screen readers. Visible by default.
     */
    ariaHidden?: ('title' | 'subtitle' | 'logo' | 'backgroundImage' | 'description' | 'messagesHeader' | 'messagesSubtitle')[]
}

/** 
* Presents a preview of an institution profile page
* 
* ## Example Usage
* ```jsx
*        &lt;CBInstitutionPreview 
*            name="Institution Preview"
*            width={width}
*            title={universityName}
*            titleDescription={"Organization Name"}
*            subtitle={universityLocation}
*            subtitleDescription={"City, State"}
*            description="'Lorem ipsum dolor sit amet, consectetur adipiscing elit, sed do eiusmod tempor incididunt ut labore et dolore magna aliqua. et dolore magna aliqua. et dolore magna aliqua."
*            logo={squareLogo}
*            logoDescription={"Square Logo"}
*            background={institutionImage}
*            backgroundDescription={"Background Image"}
*            ariaHidden={['title', 'subtitle', 'description', 'backgroundImage', 'logo', 'messagesHeader', 'messagesSubtitle']}
*            /&gt;
* ```
* 
* @component
*/
function CBInstitutionPreview(props: CBInstitutionPreviewProps) : React.ReactNode {
    const ariaHiddenHeaderMap = {
        'title': 'title',
        'subtitle': 'subtitle',
        'logo': 'logo',
        'backgroundImage' : 'backgroundImage'
    }
    const ariaHiddenDescriptionMap = {
        'description' : 'title'
    }

    const ariaHiddenMessagesMap = {
        'messagesHeader' : 'title',
        'messagesSubtitle' : 'subtitle',
    }

    let sections = [
        {
            'id': 'institution-header',
            'type': 'institution-header',
            'props': { title: props.title, 
                       titleDescription: props.titleDescription,       
                       subtitle: props.subtitle, 
                       subtitleDescription: props.subtitleDescription,
                       logo: props.logo, 
                       logoDescription: props.logoDescription, 
                       background: props.background,
                       backgroundDescription: props.backgroundDescription,
                       ariaHidden : mapAriaHiddenArray(ariaHiddenHeaderMap, props.ariaHidden)
                     }
        },
        {
            'id': 'divider',
            'type': 'divider'
        },
        {
            'id': 'institution-description',
            'type': 'paragraph-with-more',
            'props': { 
                title: props.description ?? "Lorem ipsum dolor sit amet, consectetur adipiscing elit, sed do eiusmod tempor incididunt ut labore et dolore magna aliqua.",
                ariaHidden : mapAriaHiddenArray(ariaHiddenDescriptionMap, props.ariaHidden)                
            }
        },
        {
            'id': 'divider',
            'type': 'divider'
        },
        {
            'id': 'section-header',
            'type': 'section-header',
            'props': { title: 'Messages', 
                       subtitle: `Sent to you from ${props.title}`,
                       ariaHidden : mapAriaHiddenArray(ariaHiddenMessagesMap, props.ariaHidden)                                    
                    }
        },
        {
            'id': 'message',
            'type': 'message-card',
            'props': { 'title': '' }
        },        
        {
            'id': 'message',
            'type': 'message-card',
            'props': { 'title': '' }
        },  
        {
            'id': 'message',
            'type': 'message-card',
            'props': { 'title': '' }
        }               
    ]

    let page = {
        id: 'page-1',
        sections: sections
    }

    return <YapContentPreview components={institutionComponents} styles={styles} fonts={fonts} {...props} page={page} />
}

export default CBInstitutionPreview